import React from 'react';
import { Chip, makeStyles } from '@material-ui/core';
import { Invoice } from '../../models/InvoiceModel';
import { useCommonStyles } from '../../styles/CommonStyles';
import { isAfterTomorrow } from '../../util/DateUtil';

import { InvoiceActionCallbacks, INVOICE_STATES, InvoiceStateChecker } from './InvoiceActionTypes';
import InvoiceActionButtons from './InvoiceActionButtons';

interface InvoiceStateTagProps extends InvoiceActionCallbacks {
  invoice: Invoice;
}

// MUIのスタイル定義
const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  tagContainer: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  chip: {
    fontWeight: 'bold',
    fontSize: '0.85rem',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5),
    color: '#000',
  },
  unpaid: {
    backgroundColor: theme.palette.error.main,
  },
  paid: {
    backgroundColor: theme.palette.success.main,
  },
  pending: {
    backgroundColor: theme.palette.warning.main,
  },
  default: {
    backgroundColor: theme.palette.grey[500],
  },
}));

/**
 * Invoice の状態を表示し、必要に応じてアクションボタンを提供するコンポーネント
 * 受領権限を持つユーザーが確認状態のInvoiceを操作できる
 */
const InvoiceStateTag: React.FC<InvoiceStateTagProps> = ({ invoice, onReject, onReceive }) => {
  const commonClasses = useCommonStyles();
  const classes = useStyles();

  /**
   * 状態に応じたタグのプロパティを取得
   */
  const getTagProperties = (invoice: Invoice) => {
    const state = invoice.state;

    // 請求日が明後日以降で、受領済み以外の場合は「予定」
    if (state !== INVOICE_STATES.RECEIVED && isAfterTomorrow(invoice.billing_date)) {
      return { label: '予定', className: commonClasses.scheduled };
    }

    switch (state) {
      case INVOICE_STATES.REVIEWING:
        return { label: '確認', className: commonClasses.reviewing };
      case INVOICE_STATES.RECEIVED:
        return { label: '受領', className: commonClasses.received };
      case INVOICE_STATES.REJECTED:
        return { label: '却下', className: commonClasses.rejected };
      case INVOICE_STATES.ON_HOLD:
        return { label: '保留', className: commonClasses.onHold };
      case INVOICE_STATES.RESUBMISSION:
        return { label: '再提出', className: commonClasses.resubmission };
      default:
        return { label: '不明', className: commonClasses.unknown };
    }
  };

  const { label, className } = getTagProperties(invoice);

  /**
   * 受領ボタンを表示するか判定
   * 条件： 受領権限がある、確認状態
   */
  const isShowReceiveButton =
    InvoiceStateChecker.hasReceivePermission(invoice) &&
    InvoiceStateChecker.isInReviewState(invoice);

  return (
    <div className={classes.container}>
      {/* 状態タグ表示エリア */}
      <div className={classes.tagContainer}>
        {/* メイン状態タグ */}
        {!isShowReceiveButton && (
          <Chip label={label} className={`${classes.chip} ${className}`} size="small" />
        )}

        {/* アクションボタン（受領権限があり確認状態の場合） */}
        <InvoiceActionButtons invoice={invoice} onReject={onReject} onReceive={onReceive} />
      </div>
    </div>
  );
};

export default InvoiceStateTag;
